import { socketManager, MultiHandBlackjackState, BlackjackHandState } from '../../socket/SocketManager';

/* ====================================================================== */
/*  CONSTANTS                                                              */
/* ====================================================================== */

const FELT_GREEN = 0x0a5c0a;
const FELT_DARK = 0x084a08;
const WOOD_BROWN = 0x2a1a0a;
const CARD_BACK_COLOR = 0x1a2a4a;
const CARD_BACK_PATTERN = 0x2a5a8a;
const GOLD = 0xccaa00;
const GOLD_HEX = '#ccaa00';
const GOLD_BRIGHT = 0xffd700;
const GOLD_BRIGHT_HEX = '#ffd700';

const SUIT_SYMBOLS: Record<string, string> = {
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
  spades: '\u2660',
};

function suitColor(suit: string): string {
  return suit === 'hearts' || suit === 'diamonds' ? '#cc0000' : '#222222';
}

const CHIP_DENOMINATIONS = [10, 25, 50, 100, 500];

const CHIP_COLORS: Record<number, number> = {
  10: 0xcc0000,
  25: 0x00aa00,
  50: 0x0044cc,
  100: 0x222222,
  500: 0x7b2fbe,
};

const CHIP_LABELS: Record<number, string> = {
  10: '10',
  25: '25',
  50: '50',
  100: '100',
  500: '500',
};

/* 5 seats arc: indices 0..4, center = 2 */
const SEAT_COUNT = 5;

/* Mapping from number of active spots to which seat indices to use */
const SPOT_SEAT_MAP: Record<number, number[]> = {
  1: [2],
  2: [1, 3],
  3: [1, 2, 3],
};

interface BettingSpot {
  seatIndex: number;
  amount: number;
  useDirty: boolean;
}

/* ====================================================================== */
/*  BLACKJACK GAME CLASS                                                   */
/* ====================================================================== */

export class BlackjackGame {
  private selectedChipAmount = 10;
  private useDirty = false;
  private elements: Phaser.GameObjects.GameObject[] = [];
  private tweens: Phaser.Tweens.Tween[] = [];
  private timers: Phaser.Time.TimerEvent[] = [];
  private isPlaying = false;
  private currentState: MultiHandBlackjackState | null = null;
  private previousState: MultiHandBlackjackState | null = null;
  private chipSelectors: Phaser.GameObjects.Container[] = [];

  /* Betting: each seat can have a bet placed on it (cumulative clicks) */
  private seatBets: Record<number, { amount: number; useDirty: boolean }> = {};

  /* Cached balance from GameScene */
  private balanceClean = 0;
  private balanceDirty = 0;

  constructor(
    private scene: Phaser.Scene,
    private graphics: Phaser.GameObjects.Graphics,
    private building: any,
    private stateData: any,
    private onBack: () => void,
  ) {
    // Pull balances from GameScene
    try {
      const gameScene = this.scene.scene.get('GameScene') as any;
      this.balanceDirty = gameScene.moneyDirty ?? 0;
      this.balanceClean = gameScene.moneyClean ?? 0;
    } catch { /* ok */ }
  }

  /* ------------------------------------------------------------------ */
  /*  PUBLIC API (called by CasinoInterior)                              */
  /* ------------------------------------------------------------------ */

  render(): void {
    const existingState: MultiHandBlackjackState | undefined = this.stateData?.multiHandBlackjackState;
    console.log(`[BLACKJACK] render called, existingState=${existingState ? `complete=${existingState.isAllComplete}, hands=${existingState.hands.length}` : 'none'}`);
    if (existingState && !existingState.isAllComplete) {
      this.isPlaying = true;
      this.currentState = existingState;
      this.clearElements();
      this.renderGame(existingState);
    } else {
      this.clearElements();
      this.renderBetScreen();
    }
  }

  destroy(): void {
    this.clearElements();
  }

  /* ================================================================== */
  /*  TABLE BACKGROUND                                                   */
  /* ================================================================== */

  private drawTable(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;

    /* Full green felt background */
    this.graphics.fillStyle(FELT_GREEN, 1);
    this.graphics.fillRect(0, 0, W, H);

    /* Wood border — curved at bottom (large ellipse peeking from bottom) */
    const borderThickness = Math.max(8, H * 0.012);
    this.graphics.fillStyle(WOOD_BROWN, 1);
    /* Bottom curve: ellipse centered below screen bottom */
    this.graphics.fillEllipse(W / 2, H + H * 0.35, W * 1.1, H * 0.95);

    /* Re-fill the playing area above the wood with felt */
    this.graphics.fillStyle(FELT_GREEN, 1);
    this.graphics.fillEllipse(W / 2, H + H * 0.35, W * 1.1 - borderThickness * 2, H * 0.95 - borderThickness * 2);

    /* Inner decorative border for depth */
    this.graphics.lineStyle(1, GOLD, 0.25);
    this.graphics.strokeEllipse(W / 2, H + H * 0.35, W * 1.1 - borderThickness * 4, H * 0.95 - borderThickness * 4);

    /* Subtle darker felt band across upper table area */
    this.graphics.fillStyle(FELT_DARK, 0.15);
    this.graphics.fillRect(0, H * 0.08, W, H * 0.22);
  }

  private drawTableText(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;
    const baseFontSize = Math.max(10, Math.round(W * 0.012));

    /* "BLACKJACK PAYS 3 TO 2" — large, gold, centered */
    const paysTxt = this.scene.add.text(cx, H * 0.32, 'BLACKJACK PAYS 3 TO 2', {
      fontSize: Math.max(16, Math.round(W * 0.022)) + 'px',
      fontFamily: 'serif',
      color: GOLD_BRIGHT_HEX,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(99).setAlpha(0.65);
    this.elements.push(paysTxt);

    /* "Dealer must stand on 17 and draw to 16" */
    const ruleTxt = this.scene.add.text(cx, H * 0.36, 'Dealer must stand on 17 and draw to 16', {
      fontSize: Math.max(9, baseFontSize) + 'px',
      fontFamily: 'serif',
      color: GOLD_HEX,
      fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(99).setAlpha(0.4);
    this.elements.push(ruleTxt);

    /* "INSURANCE PAYS 2 TO 1" — yellow, positioned just above betting spots arc */
    const insTxt = this.scene.add.text(cx, H * 0.40, 'INSURANCE PAYS 2 TO 1', {
      fontSize: Math.max(10, Math.round(W * 0.014)) + 'px',
      fontFamily: 'serif',
      color: '#ddcc44',
    }).setOrigin(0.5).setDepth(99).setAlpha(0.45);
    this.elements.push(insTxt);
  }

  /* ================================================================== */
  /*  TOP INFO BAR                                                       */
  /* ================================================================== */

  private drawInfoBar(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const barH = H * 0.05;
    const barY = H * 0.02;

    /* Semi-transparent dark background */
    const barG = this.scene.add.graphics().setDepth(110);
    barG.fillStyle(0x000000, 0.6);
    barG.fillRoundedRect(W * 0.02, barY, W * 0.96, barH, 6);
    this.elements.push(barG);

    const fontSize = Math.max(10, Math.round(W * 0.012)) + 'px';
    const credits = this.useDirty ? this.balanceDirty : this.balanceClean;
    const totalBet = this.getTotalBet();

    /* CREDITS */
    const credTxt = this.scene.add.text(W * 0.06, barY + barH / 2, `CREDITS: $${Math.floor(credits)}`, {
      fontSize,
      fontFamily: 'monospace',
      color: '#44cc44',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(credTxt);

    /* TOTAL BET */
    const betTxt = this.scene.add.text(W * 0.30, barY + barH / 2, `MISE TOTALE: $${totalBet}`, {
      fontSize,
      fontFamily: 'monospace',
      color: GOLD_BRIGHT_HEX,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(betTxt);

    /* MIN BET */
    const minBet = this.stateData?.casinoInfo?.houseEdge?.blackjack !== undefined ? 10 : 10;
    const minTxt = this.scene.add.text(W * 0.56, barY + barH / 2, `Min: $${minBet}`, {
      fontSize,
      fontFamily: 'monospace',
      color: '#aaaaaa',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(minTxt);

    /* MONEY TYPE */
    const typeTxt = this.scene.add.text(W * 0.75, barY + barH / 2, this.useDirty ? 'SALE' : 'PROPRE', {
      fontSize,
      fontFamily: 'monospace',
      color: this.useDirty ? '#cc4444' : '#44cc44',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(typeTxt);
  }

  private drawGameInfoBar(state: MultiHandBlackjackState): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const barH = H * 0.05;
    const barY = H * 0.02;

    const barG = this.scene.add.graphics().setDepth(110);
    barG.fillStyle(0x000000, 0.6);
    barG.fillRoundedRect(W * 0.02, barY, W * 0.96, barH, 6);
    this.elements.push(barG);

    const fontSize = Math.max(10, Math.round(W * 0.012)) + 'px';
    const credits = this.useDirty ? this.balanceDirty : this.balanceClean;
    const totalBet = state.hands.reduce((sum, h) => sum + h.betAmount, 0);
    const won = state.isAllComplete ? (state.totalPayout ?? 0) : 0;

    const credTxt = this.scene.add.text(W * 0.06, barY + barH / 2, `CREDITS: $${Math.floor(credits)}`, {
      fontSize,
      fontFamily: 'monospace',
      color: '#44cc44',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(credTxt);

    const betTxt = this.scene.add.text(W * 0.30, barY + barH / 2, `MISE: $${totalBet}`, {
      fontSize,
      fontFamily: 'monospace',
      color: GOLD_BRIGHT_HEX,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(betTxt);

    if (state.isAllComplete) {
      const wonColor = won > 0 ? '#44cc44' : '#ff4444';
      const wonTxt = this.scene.add.text(W * 0.56, barY + barH / 2, `GAIN: $${won}`, {
        fontSize,
        fontFamily: 'monospace',
        color: wonColor,
        fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(111);
      this.elements.push(wonTxt);
    }

    const typeTxt = this.scene.add.text(W * 0.78, barY + barH / 2, this.useDirty ? 'SALE' : 'PROPRE', {
      fontSize,
      fontFamily: 'monospace',
      color: this.useDirty ? '#cc4444' : '#44cc44',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(111);
    this.elements.push(typeTxt);
  }

  /* ================================================================== */
  /*  SHOE INDICATOR                                                     */
  /* ================================================================== */

  private drawShoe(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const shoeW = Math.max(30, W * 0.035);
    const shoeH = Math.max(20, H * 0.03);
    const shoeX = W * 0.88;
    const shoeY = H * 0.09;

    const shoeG = this.scene.add.graphics().setDepth(100);
    shoeG.fillStyle(0x1a1a1a, 0.8);
    shoeG.fillRoundedRect(shoeX, shoeY, shoeW, shoeH, 4);
    shoeG.lineStyle(1, 0x444444, 0.6);
    shoeG.strokeRoundedRect(shoeX, shoeY, shoeW, shoeH, 4);
    /* Cards peeking out */
    shoeG.fillStyle(0xffffff, 0.3);
    shoeG.fillRect(shoeX + 2, shoeY + 2, shoeW * 0.15, shoeH - 4);
    this.elements.push(shoeG);

    const shoeTxt = this.scene.add.text(shoeX + shoeW / 2, shoeY + shoeH + 4, 'Shoe', {
      fontSize: Math.max(7, Math.round(W * 0.008)) + 'px',
      fontFamily: 'monospace',
      color: '#888888',
    }).setOrigin(0.5, 0).setDepth(100);
    this.elements.push(shoeTxt);
  }

  /* ================================================================== */
  /*  SEAT POSITIONS (5-seat arc)                                        */
  /* ================================================================== */

  private getSeatPosition(seatIndex: number): { x: number; y: number } {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;
    const arcCenterY = H * 0.51;
    const arcRadiusX = W * 0.38;
    const arcRadiusY = H * 0.08;

    /* 5 seats spread from ~150 deg to ~30 deg (left to right) */
    const angles = [150, 120, 90, 60, 30];
    const angleRad = (angles[seatIndex] * Math.PI) / 180;

    return {
      x: cx + arcRadiusX * Math.cos(angleRad),
      y: arcCenterY - arcRadiusY * Math.sin(angleRad),
    };
  }

  /* ================================================================== */
  /*  BETTING SCREEN                                                     */
  /* ================================================================== */

  private renderBetScreen(): void {
    this.isPlaying = false;
    this.drawTable();
    this.drawTableText();
    this.drawInfoBar();
    this.drawShoe();

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const spotRadius = Math.min(W * 0.04, 36);

    /* Back button */
    this.renderBackButton();

    /* 5 seats */
    for (let i = 0; i < SEAT_COUNT; i++) {
      const pos = this.getSeatPosition(i);
      const hasBet = this.seatBets[i] !== undefined && this.seatBets[i].amount > 0;
      const isPlayable = i >= 1 && i <= 3; /* seats 1, 2, 3 are playable */
      this.renderBettingSeat(pos.x, pos.y, i, spotRadius, hasBet, isPlayable);
    }

    /* Chip selector */
    this.renderChipSelector(W / 2, H * 0.75);

    /* Propre/Sale toggle */
    this.renderMoneyToggle(W / 2, H * 0.81);

    /* DISTRIBUER (Deal) button */
    const activeCount = this.getActiveSeatCount();
    this.renderDistribuerButton(W / 2, H * 0.88, activeCount > 0);
  }

  private renderBettingSeat(
    x: number, y: number, seatIndex: number, radius: number,
    hasBet: boolean, isPlayable: boolean,
  ): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const container = this.scene.add.container(x, y).setDepth(100);
    const g = this.scene.add.graphics();

    if (hasBet && isPlayable) {
      /* Active seat with bet */
      g.lineStyle(3, GOLD_BRIGHT, 0.8);
      g.strokeCircle(0, 0, radius + 4);
      g.fillStyle(0x0d7a0d, 0.9);
      g.fillCircle(0, 0, radius);
      g.lineStyle(2, GOLD_BRIGHT, 0.9);
      g.strokeCircle(0, 0, radius);
      g.lineStyle(1, GOLD_BRIGHT, 0.3);
      g.strokeCircle(0, 0, radius - 6);
      container.add(g);

      const bet = this.seatBets[seatIndex];

      /* Chip stack visual */
      const chipAmount = this.getNearestChipColor(bet.amount);
      const chipColor = CHIP_COLORS[chipAmount] || 0x888888;
      const stackCount = Math.min(5, Math.ceil(bet.amount / this.selectedChipAmount));
      const chipG = this.scene.add.graphics();
      for (let ci = stackCount - 1; ci >= 0; ci--) {
        const cy = -ci * 2.5;
        chipG.fillStyle(chipColor, 1);
        chipG.fillCircle(0, cy, radius * 0.5);
        chipG.lineStyle(1, 0xffffff, 0.7);
        chipG.strokeCircle(0, cy, radius * 0.5);
      }
      container.add(chipG);

      /* Bet amount badge */
      const badgeW = Math.max(36, (`$${bet.amount}`).length * 8);
      const badgeG = this.scene.add.graphics();
      badgeG.fillStyle(0x000000, 0.7);
      badgeG.fillRoundedRect(-badgeW / 2, radius + 4, badgeW, 18, 6);
      badgeG.lineStyle(1, GOLD_BRIGHT, 0.6);
      badgeG.strokeRoundedRect(-badgeW / 2, radius + 4, badgeW, 18, 6);
      container.add(badgeG);

      const betTxt = this.scene.add.text(0, radius + 13, `$${bet.amount}`, {
        fontSize: Math.max(10, Math.round(W * 0.011)) + 'px',
        fontFamily: 'monospace',
        color: GOLD_BRIGHT_HEX,
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(101);
      container.add(betTxt);

      /* Money type */
      const typeTxt = this.scene.add.text(0, radius + 26, bet.useDirty ? 'Sale' : 'Propre', {
        fontSize: Math.max(7, Math.round(W * 0.008)) + 'px',
        fontFamily: 'monospace',
        color: bet.useDirty ? '#cc4444' : '#44cc44',
      }).setOrigin(0.5).setDepth(101);
      container.add(typeTxt);

    } else if (isPlayable) {
      /* Empty playable seat */
      g.fillStyle(0x0d7a0d, 0.3);
      g.fillCircle(0, 0, radius);
      g.lineStyle(2, GOLD, 0.5);
      g.strokeCircle(0, 0, radius);
      g.lineStyle(1, GOLD, 0.2);
      g.strokeCircle(0, 0, radius - 6);
      container.add(g);

      const promptTxt = this.scene.add.text(0, 0, 'Cliquer\npour\njouer', {
        fontSize: Math.max(7, Math.round(W * 0.008)) + 'px',
        fontFamily: 'monospace',
        color: GOLD_HEX,
        align: 'center',
      }).setOrigin(0.5).setAlpha(0.6);
      container.add(promptTxt);
    } else {
      /* Dim non-playable seat (0 and 4) */
      g.fillStyle(0x0d7a0d, 0.12);
      g.fillCircle(0, 0, radius);
      g.lineStyle(1, GOLD, 0.15);
      g.strokeCircle(0, 0, radius);
      container.add(g);

      const dimTxt = this.scene.add.text(0, 0, 'Click\nto\nPlay', {
        fontSize: Math.max(7, Math.round(W * 0.007)) + 'px',
        fontFamily: 'monospace',
        color: '#666666',
        align: 'center',
      }).setOrigin(0.5).setAlpha(0.4);
      container.add(dimTxt);
    }

    /* Seat label */
    const labelTxt = this.scene.add.text(0, -radius - Math.max(8, H * 0.01), `Seat ${seatIndex + 1}`, {
      fontSize: Math.max(8, Math.round(W * 0.009)) + 'px',
      fontFamily: 'monospace',
      color: isPlayable ? GOLD_HEX : '#555555',
    }).setOrigin(0.5).setAlpha(0.5);
    container.add(labelTxt);

    /* Hit area */
    if (isPlayable) {
      const hitArea = this.scene.add.zone(0, 0, radius * 2 + 10, radius * 2 + 10)
        .setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerdown', () => {
        if (hasBet) {
          /* Add more chips to this seat */
          this.seatBets[seatIndex].amount += this.selectedChipAmount;
          this.seatBets[seatIndex].useDirty = this.useDirty;
        } else {
          /* Place first bet on this seat */
          this.seatBets[seatIndex] = {
            amount: this.selectedChipAmount,
            useDirty: this.useDirty,
          };
        }
        this.refreshBalance();
        this.clearElements();
        this.renderBetScreen();
      });
      hitArea.on('pointerover', () => container.setAlpha(0.85));
      hitArea.on('pointerout', () => container.setAlpha(1));
    }

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  CHIP SELECTOR                                                      */
  /* ================================================================== */

  private renderChipSelector(centerX: number, y: number): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const spacing = Math.min(W * 0.05, 48);
    const startX = centerX - ((CHIP_DENOMINATIONS.length - 1) * spacing) / 2;

    /* Label */
    const label = this.scene.add.text(centerX, y - Math.max(16, H * 0.02), 'JETONS', {
      fontSize: Math.max(9, Math.round(W * 0.01)) + 'px',
      fontFamily: 'monospace',
      color: '#aaaaaa',
    }).setOrigin(0.5).setDepth(109);
    this.elements.push(label);

    CHIP_DENOMINATIONS.forEach((amount, i) => {
      const chipX = startX + i * spacing;
      const container = this.scene.add.container(chipX, y).setDepth(109);
      const color = CHIP_COLORS[amount] || 0x888888;
      const isSelected = this.selectedChipAmount === amount;
      const radius = Math.min(W * 0.02, 18);

      const g = this.scene.add.graphics();

      /* Selection glow */
      if (isSelected) {
        g.lineStyle(3, 0xffdd44, 0.9);
        g.strokeCircle(0, 0, radius + 3);
        /* Subtle outer glow */
        g.lineStyle(5, 0xffdd44, 0.2);
        g.strokeCircle(0, 0, radius + 6);
      }

      /* Chip body */
      g.fillStyle(color, 1);
      g.fillCircle(0, 0, radius);
      g.lineStyle(2, 0xffffff, 0.9);
      g.strokeCircle(0, 0, radius);
      /* Inner ring */
      g.lineStyle(1, 0xffffff, 0.35);
      g.strokeCircle(0, 0, radius - 4);

      /* Edge notches (dashed pattern around chip) */
      for (let n = 0; n < 8; n++) {
        const angle = (n / 8) * Math.PI * 2;
        const nx1 = Math.cos(angle) * (radius - 2);
        const ny1 = Math.sin(angle) * (radius - 2);
        const nx2 = Math.cos(angle) * (radius + 1);
        const ny2 = Math.sin(angle) * (radius + 1);
        g.lineStyle(2, 0xffffff, 0.5);
        g.beginPath();
        g.moveTo(nx1, ny1);
        g.lineTo(nx2, ny2);
        g.strokePath();
      }

      container.add(g);

      const txt = this.scene.add.text(0, 0, CHIP_LABELS[amount], {
        fontSize: amount >= 100 ? Math.max(8, Math.round(W * 0.008)) + 'px' : Math.max(9, Math.round(W * 0.009)) + 'px',
        fontFamily: 'monospace',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(txt);

      const hitArea = this.scene.add.zone(0, 0, radius * 2 + 8, radius * 2 + 8)
        .setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerdown', () => {
        this.selectedChipAmount = amount;
        this.clearElements();
        this.renderBetScreen();
      });
      hitArea.on('pointerover', () => container.setScale(1.1));
      hitArea.on('pointerout', () => container.setScale(1.0));

      this.chipSelectors.push(container);
      this.elements.push(container);
    });

    /* Clear bets button (small, right of chips) */
    const clearX = startX + (CHIP_DENOMINATIONS.length - 1) * spacing + spacing;
    const clearContainer = this.scene.add.container(clearX, y).setDepth(109);
    const clearRadius = Math.min(W * 0.015, 14);
    const clearG = this.scene.add.graphics();
    clearG.fillStyle(0x660000, 0.8);
    clearG.fillCircle(0, 0, clearRadius);
    clearG.lineStyle(1, 0xff4444, 0.6);
    clearG.strokeCircle(0, 0, clearRadius);
    clearContainer.add(clearG);
    const clearTxt = this.scene.add.text(0, 0, 'X', {
      fontSize: Math.max(9, Math.round(W * 0.01)) + 'px',
      fontFamily: 'monospace',
      color: '#ff4444',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    clearContainer.add(clearTxt);
    const clearHit = this.scene.add.zone(0, 0, clearRadius * 2 + 4, clearRadius * 2 + 4)
      .setInteractive({ useHandCursor: true });
    clearContainer.add(clearHit);
    clearHit.on('pointerdown', () => {
      this.seatBets = {};
      this.clearElements();
      this.renderBetScreen();
    });
    clearHit.on('pointerover', () => clearContainer.setScale(1.1));
    clearHit.on('pointerout', () => clearContainer.setScale(1.0));
    this.elements.push(clearContainer);
  }

  /* ================================================================== */
  /*  MONEY TOGGLE (Propre / Sale)                                       */
  /* ================================================================== */

  private renderMoneyToggle(cx: number, y: number): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnW = Math.min(W * 0.1, 90);
    const btnH = Math.min(28, H * 0.035);

    /* Propre */
    const propreContainer = this.scene.add.container(cx - W * 0.07, y).setDepth(100);
    const propreBg = this.scene.add.graphics();
    if (!this.useDirty) {
      propreBg.fillStyle(0x44cc44, 1);
      propreBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    } else {
      propreBg.fillStyle(0x1a3a1a, 1);
      propreBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
      propreBg.lineStyle(1, 0x44cc44, 0.5);
      propreBg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    }
    propreContainer.add(propreBg);
    const propreTxt = this.scene.add.text(0, 0, 'PROPRE', {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color: !this.useDirty ? '#000000' : '#44cc44',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    propreContainer.add(propreTxt);
    const propreHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    propreContainer.add(propreHit);
    propreHit.on('pointerdown', () => {
      this.useDirty = false;
      this.clearElements();
      this.renderBetScreen();
    });
    propreHit.on('pointerover', () => propreContainer.setAlpha(0.8));
    propreHit.on('pointerout', () => propreContainer.setAlpha(1));
    this.elements.push(propreContainer);

    /* Sale */
    const saleContainer = this.scene.add.container(cx + W * 0.07, y).setDepth(100);
    const saleBg = this.scene.add.graphics();
    if (this.useDirty) {
      saleBg.fillStyle(0xcc4444, 1);
      saleBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    } else {
      saleBg.fillStyle(0x3a1a1a, 1);
      saleBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
      saleBg.lineStyle(1, 0xcc4444, 0.5);
      saleBg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    }
    saleContainer.add(saleBg);
    const saleTxt = this.scene.add.text(0, 0, 'SALE', {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color: this.useDirty ? '#000000' : '#cc4444',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    saleContainer.add(saleTxt);
    const saleHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    saleContainer.add(saleHit);
    saleHit.on('pointerdown', () => {
      this.useDirty = true;
      this.clearElements();
      this.renderBetScreen();
    });
    saleHit.on('pointerover', () => saleContainer.setAlpha(0.8));
    saleHit.on('pointerout', () => saleContainer.setAlpha(1));
    this.elements.push(saleContainer);
  }

  /* ================================================================== */
  /*  DISTRIBUER (Deal) BUTTON                                           */
  /* ================================================================== */

  private renderDistribuerButton(cx: number, y: number, enabled: boolean): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnW = Math.min(W * 0.18, 160);
    const btnH = Math.min(36, H * 0.04);

    const container = this.scene.add.container(cx, y).setDepth(100);
    const bg = this.scene.add.graphics();
    if (enabled) {
      bg.fillStyle(GOLD_BRIGHT, 1);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      bg.lineStyle(2, 0xffee66, 0.6);
      bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
    } else {
      bg.fillStyle(0x444444, 1);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
    }
    container.add(bg);

    const txt = this.scene.add.text(0, 0, 'DISTRIBUER', {
      fontSize: Math.max(12, Math.round(W * 0.015)) + 'px',
      fontFamily: 'monospace',
      color: enabled ? '#000000' : '#888888',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    const hitArea = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: enabled });
    container.add(hitArea);

    if (enabled) {
      hitArea.on('pointerdown', () => {
        const hands = this.buildHandsFromBets();
        console.log(`[BLACKJACK] DISTRIBUER clicked: hands=${JSON.stringify(hands)}`);
        this.sendAction('NEW_HAND', { hands });
      });
      hitArea.on('pointerover', () => container.setScale(1.05));
      hitArea.on('pointerout', () => container.setScale(1.0));
    }

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  BACK BUTTON                                                        */
  /* ================================================================== */

  private renderBackButton(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnW = Math.min(W * 0.1, 90);
    const btnH = Math.min(24, H * 0.03);

    const container = this.scene.add.container(W * 0.08, H * 0.09).setDepth(115);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x222222, 0.8);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 6);
    bg.lineStyle(1, 0x7b2fbe, 0.6);
    bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 6);
    container.add(bg);

    const txt = this.scene.add.text(0, 0, '\u2190 RETOUR', {
      fontSize: Math.max(10, Math.round(W * 0.011)) + 'px',
      fontFamily: 'monospace',
      color: '#ccaaff',
    }).setOrigin(0.5);
    container.add(txt);

    const hitArea = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    container.add(hitArea);
    hitArea.on('pointerdown', () => this.onBack());
    hitArea.on('pointerover', () => container.setAlpha(0.8));
    hitArea.on('pointerout', () => container.setAlpha(1));

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  GAME RENDERING (during play)                                       */
  /* ================================================================== */

  private renderGame(state: MultiHandBlackjackState): void {
    this.isPlaying = true;
    this.drawTable();
    this.drawTableText();
    this.drawGameInfoBar(state);
    this.drawShoe();
    this.renderBackButton();

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;
    const cardW = Math.min(W * 0.045, 60);
    const cardH = cardW * 1.44;
    const cardSpacing = Math.min(cardW * 1.15, W * 0.055);

    /* ---- Dealer area (H*0.08 to H*0.30) ---- */
    const dealerLabel = this.scene.add.text(cx, H * 0.10, 'CROUPIER', {
      fontSize: Math.max(12, Math.round(W * 0.015)) + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(100);
    this.elements.push(dealerLabel);

    /* Dealer score badge */
    const dealerScoreText = state.dealerScore !== null ? `${state.dealerScore}` : '?';
    this.renderScoreBadge(cx + W * 0.12, H * 0.10, dealerScoreText);

    /* Dealer cards */
    const dealerCardY = H * 0.19;
    const dealerCardStartX = cx - ((state.dealerCards.length - 1) * cardSpacing) / 2;
    state.dealerCards.forEach((card, i) => {
      const targetX = dealerCardStartX + i * cardSpacing;
      const container = this.drawCard(targetX, dealerCardY, card, cardW, cardH);
      this.elements.push(container);
    });

    /* ---- Player hands ---- */
    const numHands = state.hands.length;
    const handSpotPositions = this.getHandSpotPositions(state);
    const playerCardY = H * 0.36;
    const spotCircleY = H * 0.51;

    /* Draw all 5 seat circles (dim ones that are not used) */
    for (let s = 0; s < SEAT_COUNT; s++) {
      const pos = this.getSeatPosition(s);
      const isUsed = handSpotPositions.some(hp => hp.seatIndex === s);
      if (!isUsed) {
        this.renderDimSeat(pos.x, pos.y);
      }
    }

    /* Render each hand */
    state.hands.forEach((hand, hi) => {
      const hp = handSpotPositions[hi];
      if (!hp) return;
      const spotX = hp.x;
      const spotY = hp.y;
      const isActive = state.activeHandIndex === hi && !state.isAllComplete;

      /* Active hand glow */
      if (isActive) {
        const glowG = this.scene.add.graphics().setDepth(98);
        glowG.lineStyle(3, GOLD_BRIGHT, 0.7);
        glowG.strokeCircle(spotX, spotY, Math.min(W * 0.04, 36) + 8);
        glowG.lineStyle(6, GOLD_BRIGHT, 0.2);
        glowG.strokeCircle(spotX, spotY, Math.min(W * 0.04, 36) + 14);
        this.elements.push(glowG);
      }

      /* Spot circle with chip */
      this.renderGameSpot(spotX, spotY, hand, isActive);

      /* Cards fanned above the spot */
      const cardY = playerCardY + (hand.isSplitHand ? hi * cardH * 0.12 : 0);
      const fanSpacing = cardSpacing * 0.75;
      const handCardStartX = spotX - ((hand.cards.length - 1) * fanSpacing) / 2;
      hand.cards.forEach((card, ci) => {
        const targetX = handCardStartX + ci * fanSpacing;
        const container = this.drawCard(targetX, cardY, card, cardW, cardH);
        this.elements.push(container);
      });

      /* Score badge */
      const scoreBadgeX = spotX + ((hand.cards.length - 1) * fanSpacing) / 2 + cardW * 0.6;
      this.renderScoreBadge(scoreBadgeX, cardY, `${hand.score}`);

      /* Bet badge in corner of hand area */
      const betBadgeX = spotX - ((hand.cards.length - 1) * fanSpacing) / 2 - cardW * 0.6;
      this.renderBetBadge(betBadgeX, cardY, hand.betAmount, hand.usedDirty);

      /* Hand label for multi-hand */
      if (numHands > 1) {
        const handLabel = this.scene.add.text(spotX, cardY - cardH / 2 - Math.max(10, H * 0.012),
          hand.isSplitHand ? `Split ${hand.splitDepth}` : `Main ${hand.spotIndex + 1}`, {
            fontSize: Math.max(9, Math.round(W * 0.01)) + 'px',
            fontFamily: 'monospace',
            color: isActive ? GOLD_BRIGHT_HEX : '#aaaaaa',
            fontStyle: isActive ? 'bold' : 'normal',
          }).setOrigin(0.5).setDepth(100);
        this.elements.push(handLabel);
      }

      /* Outcome per hand */
      if (hand.isComplete && state.isAllComplete) {
        this.renderHandOutcome(spotX, cardY - cardH / 2 - (numHands > 1 ? Math.max(24, H * 0.03) : Math.max(14, H * 0.016)), hand);
      }
    });

    /* ---- Insurance phase ---- */
    if (state.insuranceOffered && !state.insurancePhaseComplete) {
      this.renderInsuranceOverlay(state);
      return; /* Don't render action buttons during insurance */
    }

    /* ---- Action buttons or final outcome ---- */
    if (state.isAllComplete) {
      this.renderFinalOutcome(state);
    } else {
      this.renderActionButtons(state);

      /* Hand index indicator */
      if (numHands > 1 && state.activeHandIndex >= 0) {
        const indicatorTxt = this.scene.add.text(cx, H * 0.93,
          `Main ${state.activeHandIndex + 1} / ${numHands}`, {
            fontSize: Math.max(11, Math.round(W * 0.013)) + 'px',
            fontFamily: 'monospace',
            color: GOLD_BRIGHT_HEX,
            fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(100);
        this.elements.push(indicatorTxt);
      }
    }
  }

  /* Get positions for each hand, mapping to seats */
  private getHandSpotPositions(state: MultiHandBlackjackState): { x: number; y: number; seatIndex: number }[] {
    const hands = state.hands;
    /* Group hands by spotIndex to handle splits */
    const spotGroups: Record<number, BlackjackHandState[]> = {};
    hands.forEach(h => {
      if (!spotGroups[h.spotIndex]) spotGroups[h.spotIndex] = [];
      spotGroups[h.spotIndex].push(h);
    });

    const uniqueSpots = Object.keys(spotGroups).map(Number).sort((a, b) => a - b);
    const numSpots = uniqueSpots.length;
    const seatMapping = SPOT_SEAT_MAP[numSpots] || SPOT_SEAT_MAP[3] || [1, 2, 3];

    const positions: { x: number; y: number; seatIndex: number }[] = [];
    const W = this.scene.scale.width;

    hands.forEach(hand => {
      const spotGroupIndex = uniqueSpots.indexOf(hand.spotIndex);
      const seatIndex = seatMapping[spotGroupIndex] ?? 2;
      const seatPos = this.getSeatPosition(seatIndex);

      /* If split, offset sub-hands horizontally */
      const group = spotGroups[hand.spotIndex];
      if (group.length > 1) {
        const indexInGroup = group.indexOf(hand);
        const totalInGroup = group.length;
        const spreadOffset = W * 0.06;
        const offset = (indexInGroup - (totalInGroup - 1) / 2) * spreadOffset;
        positions.push({ x: seatPos.x + offset, y: seatPos.y, seatIndex });
      } else {
        positions.push({ x: seatPos.x, y: seatPos.y, seatIndex });
      }
    });

    return positions;
  }

  private renderDimSeat(x: number, y: number): void {
    const W = this.scene.scale.width;
    const radius = Math.min(W * 0.03, 28);
    const g = this.scene.add.graphics().setDepth(97);
    g.fillStyle(0x0d7a0d, 0.1);
    g.fillCircle(x, y, radius);
    g.lineStyle(1, GOLD, 0.1);
    g.strokeCircle(x, y, radius);
    this.elements.push(g);
  }

  private renderGameSpot(x: number, y: number, hand: BlackjackHandState, isActive: boolean): void {
    const W = this.scene.scale.width;
    const radius = Math.min(W * 0.035, 30);
    const container = this.scene.add.container(x, y).setDepth(100);

    const g = this.scene.add.graphics();
    g.fillStyle(0x0d7a0d, isActive ? 0.8 : 0.4);
    g.fillCircle(0, 0, radius);
    g.lineStyle(2, isActive ? GOLD_BRIGHT : GOLD, isActive ? 0.9 : 0.4);
    g.strokeCircle(0, 0, radius);
    container.add(g);

    /* Chip in spot */
    const chipAmount = this.getNearestChipColor(hand.betAmount);
    const chipColor = CHIP_COLORS[chipAmount] || CHIP_COLORS[100];
    const chipG = this.scene.add.graphics();
    chipG.fillStyle(chipColor, 1);
    chipG.fillCircle(0, 0, radius * 0.55);
    chipG.lineStyle(1, 0xffffff, 0.7);
    chipG.strokeCircle(0, 0, radius * 0.55);
    chipG.lineStyle(1, 0xffffff, 0.3);
    chipG.strokeCircle(0, 0, radius * 0.35);
    container.add(chipG);

    const betTxt = this.scene.add.text(0, 0, `${hand.betAmount}`, {
      fontSize: Math.max(8, Math.round(W * 0.009)) + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(betTxt);

    /* Money type below */
    const typeTxt = this.scene.add.text(0, radius + Math.max(8, W * 0.008), hand.usedDirty ? 'Sale' : 'Propre', {
      fontSize: Math.max(7, Math.round(W * 0.007)) + 'px',
      fontFamily: 'monospace',
      color: hand.usedDirty ? '#cc4444' : '#44cc44',
    }).setOrigin(0.5);
    container.add(typeTxt);

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  BET BADGE (shown near cards)                                       */
  /* ================================================================== */

  private renderBetBadge(x: number, y: number, amount: number, useDirty: boolean): void {
    const W = this.scene.scale.width;
    const container = this.scene.add.container(x, y).setDepth(102);
    const badgeW = Math.max(30, W * 0.035);
    const badgeH = Math.max(16, W * 0.018);

    const g = this.scene.add.graphics();
    g.fillStyle(0x000000, 0.6);
    g.fillRoundedRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 4);
    g.lineStyle(1, useDirty ? 0xcc4444 : 0x44cc44, 0.5);
    g.strokeRoundedRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 4);
    container.add(g);

    const txt = this.scene.add.text(0, 0, `$${amount}`, {
      fontSize: Math.max(8, Math.round(W * 0.009)) + 'px',
      fontFamily: 'monospace',
      color: GOLD_BRIGHT_HEX,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  SCORE BADGE                                                        */
  /* ================================================================== */

  private renderScoreBadge(x: number, y: number, score: string): void {
    const W = this.scene.scale.width;
    const radius = Math.max(12, Math.min(W * 0.017, 18));
    const container = this.scene.add.container(x, y).setDepth(101);
    const g = this.scene.add.graphics();
    g.fillStyle(0x000000, 0.6);
    g.fillCircle(0, 0, radius);
    g.lineStyle(2, GOLD_BRIGHT, 0.8);
    g.strokeCircle(0, 0, radius);
    container.add(g);
    const txt = this.scene.add.text(0, 0, score, {
      fontSize: Math.max(10, Math.round(radius * 0.9)) + 'px',
      fontFamily: 'monospace',
      color: GOLD_BRIGHT_HEX,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);
    this.elements.push(container);
  }

  /* ================================================================== */
  /*  ACTION BUTTONS                                                     */
  /* ================================================================== */

  private renderActionButtons(state: MultiHandBlackjackState): void {
    const activeHand = state.activeHandIndex >= 0 ? state.hands[state.activeHandIndex] : null;
    if (!activeHand) return;

    console.log(`[BLACKJACK] renderActionButtons: activeHand=${state.activeHandIndex}, canHit=${activeHand.canHit}, canDoubleDown=${activeHand.canDoubleDown}, canSplit=${activeHand.canSplit}, canSurrender=${activeHand.canSurrender}, score=${activeHand.score}`);

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnY = H * 0.86;

    /* Collect visible buttons */
    interface BtnDef {
      label: string;
      color: number;
      activeColor: number;
      textColor: string;
      disabledTextColor: string;
      action: string;
      enabled: boolean;
    }

    const buttons: BtnDef[] = [];

    /* SURRENDER */
    if (activeHand.canSurrender) {
      buttons.push({
        label: 'ABANDON',
        color: 0xcc6600,
        activeColor: 0xff8800,
        textColor: '#ffffff',
        disabledTextColor: '#555555',
        action: 'SURRENDER',
        enabled: true,
      });
    }

    /* STAND — always available */
    buttons.push({
      label: 'RESTER',
      color: 0xc0392b,
      activeColor: 0xff6655,
      textColor: '#ffffff',
      disabledTextColor: '#555555',
      action: 'STAND',
      enabled: true,
    });

    /* HIT */
    buttons.push({
      label: 'TIRER',
      color: activeHand.canHit ? 0x2d8f2d : 0x1a3a1a,
      activeColor: 0x44cc44,
      textColor: '#ffffff',
      disabledTextColor: '#555555',
      action: 'HIT',
      enabled: activeHand.canHit,
    });

    /* SPLIT */
    if (activeHand.canSplit) {
      buttons.push({
        label: 'SPLIT',
        color: 0x0044cc,
        activeColor: 0x4488ff,
        textColor: '#ffffff',
        disabledTextColor: '#555555',
        action: 'SPLIT',
        enabled: true,
      });
    }

    /* DOUBLE */
    buttons.push({
      label: 'DOUBLER',
      color: activeHand.canDoubleDown ? 0xe6b800 : 0x3a3a1a,
      activeColor: 0xffdd44,
      textColor: activeHand.canDoubleDown ? '#000000' : '#555555',
      disabledTextColor: '#555555',
      action: 'DOUBLE_DOWN',
      enabled: activeHand.canDoubleDown,
    });

    /* Layout buttons evenly */
    const totalButtons = buttons.length;
    const btnW = Math.min(W * 0.14, 110);
    const btnH = Math.min(32, H * 0.04);
    const spacing = Math.min(W * 0.15, btnW + 12);
    const startX = W / 2 - ((totalButtons - 1) * spacing) / 2;

    buttons.forEach((btn, i) => {
      const bx = startX + i * spacing;
      this.renderActionButton(bx, btnY, btnW, btnH, btn);
    });
  }

  private renderActionButton(
    x: number, y: number, btnW: number, btnH: number,
    btn: { label: string; color: number; activeColor: number; textColor: string; disabledTextColor: string; action: string; enabled: boolean },
  ): void {
    const W = this.scene.scale.width;
    const container = this.scene.add.container(x, y).setDepth(100);
    const bg = this.scene.add.graphics();
    bg.fillStyle(btn.color, 1);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    if (btn.enabled) {
      bg.lineStyle(1, btn.activeColor, 0.5);
      bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    }
    container.add(bg);

    const txt = this.scene.add.text(0, 0, btn.label, {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color: btn.enabled ? btn.textColor : btn.disabledTextColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    const hitArea = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: btn.enabled });
    container.add(hitArea);

    if (btn.enabled) {
      hitArea.on('pointerdown', () => {
        console.log(`[BLACKJACK] ${btn.action} clicked`);
        this.sendAction(btn.action);
      });
      hitArea.on('pointerover', () => container.setScale(1.05));
      hitArea.on('pointerout', () => container.setScale(1.0));
    }

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  INSURANCE OVERLAY                                                  */
  /* ================================================================== */

  private renderInsuranceOverlay(state: MultiHandBlackjackState): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;

    /* Semi-transparent overlay */
    const overlay = this.scene.add.graphics().setDepth(150);
    overlay.fillStyle(0x000000, 0.5);
    overlay.fillRect(0, H * 0.60, W, H * 0.40);
    this.elements.push(overlay);

    /* Insurance question */
    const questionTxt = this.scene.add.text(cx, H * 0.68, 'INSURANCE ?', {
      fontSize: Math.max(18, Math.round(W * 0.025)) + 'px',
      fontFamily: 'serif',
      color: '#ddcc44',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(151);
    this.elements.push(questionTxt);

    const subTxt = this.scene.add.text(cx, H * 0.73, 'Le croupier montre un As', {
      fontSize: Math.max(11, Math.round(W * 0.013)) + 'px',
      fontFamily: 'monospace',
      color: '#cccccc',
    }).setOrigin(0.5).setDepth(151);
    this.elements.push(subTxt);

    const btnW = Math.min(W * 0.14, 120);
    const btnH = Math.min(36, H * 0.04);

    /* OUI button */
    const ouiContainer = this.scene.add.container(cx - W * 0.1, H * 0.80).setDepth(151);
    const ouiBg = this.scene.add.graphics();
    ouiBg.fillStyle(0x44cc44, 1);
    ouiBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
    ouiContainer.add(ouiBg);
    const ouiTxt = this.scene.add.text(0, 0, 'OUI', {
      fontSize: Math.max(14, Math.round(W * 0.017)) + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    ouiContainer.add(ouiTxt);
    const ouiHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    ouiContainer.add(ouiHit);
    ouiHit.on('pointerdown', () => {
      console.log('[BLACKJACK] INSURANCE: OUI');
      this.sendAction('INSURANCE', { accept: true });
    });
    ouiHit.on('pointerover', () => ouiContainer.setScale(1.05));
    ouiHit.on('pointerout', () => ouiContainer.setScale(1.0));
    this.elements.push(ouiContainer);

    /* NON button */
    const nonContainer = this.scene.add.container(cx + W * 0.1, H * 0.80).setDepth(151);
    const nonBg = this.scene.add.graphics();
    nonBg.fillStyle(0xcc4444, 1);
    nonBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
    nonContainer.add(nonBg);
    const nonTxt = this.scene.add.text(0, 0, 'NON', {
      fontSize: Math.max(14, Math.round(W * 0.017)) + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    nonContainer.add(nonTxt);
    const nonHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    nonContainer.add(nonHit);
    nonHit.on('pointerdown', () => {
      console.log('[BLACKJACK] INSURANCE: NON');
      this.sendAction('INSURANCE', { accept: false });
    });
    nonHit.on('pointerover', () => nonContainer.setScale(1.05));
    nonHit.on('pointerout', () => nonContainer.setScale(1.0));
    this.elements.push(nonContainer);
  }

  /* ================================================================== */
  /*  HAND OUTCOME (per hand, after game complete)                       */
  /* ================================================================== */

  private renderHandOutcome(x: number, y: number, hand: BlackjackHandState): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    let text = '';
    let color = '#ffffff';

    if (hand.outcome === 'WIN') {
      if (hand.score === 21 && hand.cards.length === 2 && !hand.isSplitHand) {
        text = 'BLACKJACK!';
        color = GOLD_BRIGHT_HEX;
      } else {
        text = 'VICTOIRE';
        color = '#44cc44';
      }
    } else if (hand.outcome === 'LOSE') {
      if (hand.score > 21) {
        text = 'BUST';
        color = '#ff4444';
      } else {
        text = 'D\u00c9FAITE';
        color = '#ff4444';
      }
    } else if (hand.outcome === 'PUSH') {
      text = '\u00c9GALIT\u00c9';
      color = '#aaaaaa';
    } else if (hand.outcome === 'SURRENDER') {
      text = 'ABANDON';
      color = '#cc6600';
    } else {
      text = hand.outcome || '';
    }

    const container = this.scene.add.container(x, y).setDepth(105);

    const pillW = Math.max(W * 0.07, text.length * Math.max(8, W * 0.009));
    const pillH = Math.max(20, H * 0.025);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.75);
    bg.fillRoundedRect(-pillW / 2, -pillH / 2, pillW, pillH, 8);
    container.add(bg);

    const outcomeTxt = this.scene.add.text(0, -2, text, {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(outcomeTxt);

    if (hand.payout !== undefined && hand.payout > 0) {
      const payTxt = this.scene.add.text(0, pillH / 2 + 2, `+$${hand.payout}`, {
        fontSize: Math.max(9, Math.round(W * 0.01)) + 'px',
        fontFamily: 'monospace',
        color: '#44cc44',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(payTxt);
    }

    this.elements.push(container);
  }

  /* ================================================================== */
  /*  FINAL OUTCOME (all hands done)                                     */
  /* ================================================================== */

  private renderFinalOutcome(state: MultiHandBlackjackState): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;

    /* Total payout display */
    if (state.totalPayout !== undefined && state.totalPayout > 0) {
      const totalContainer = this.scene.add.container(cx, H * 0.68).setDepth(105);
      const pillW = Math.max(W * 0.2, 180);
      const pillH = Math.max(32, H * 0.04);
      const totalBg = this.scene.add.graphics();
      totalBg.fillStyle(0x000000, 0.7);
      totalBg.fillRoundedRect(-pillW / 2, -pillH / 2, pillW, pillH, 12);
      totalBg.lineStyle(2, GOLD_BRIGHT, 0.6);
      totalBg.strokeRoundedRect(-pillW / 2, -pillH / 2, pillW, pillH, 12);
      totalContainer.add(totalBg);

      const totalTxt = this.scene.add.text(0, 0, `Total: +$${state.totalPayout}`, {
        fontSize: Math.max(14, Math.round(W * 0.02)) + 'px',
        fontFamily: 'monospace',
        color: '#44cc44',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      totalContainer.add(totalTxt);
      this.elements.push(totalContainer);

      const tw = this.scene.tweens.add({
        targets: totalContainer,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 400,
        yoyo: true,
        ease: 'Power2',
      });
      this.tweens.push(tw);
    } else {
      const lostTxt = this.scene.add.text(cx, H * 0.68, 'Pas de gains', {
        fontSize: Math.max(13, Math.round(W * 0.016)) + 'px',
        fontFamily: 'monospace',
        color: '#ff4444',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(105);
      this.elements.push(lostTxt);
    }

    const btnW = Math.min(W * 0.14, 120);
    const btnH = Math.min(28, H * 0.035);

    /* NOUVELLE PARTIE (Replay) */
    const replayContainer = this.scene.add.container(cx - W * 0.1, H * 0.78).setDepth(105);
    const replayBg = this.scene.add.graphics();
    replayBg.fillStyle(GOLD_BRIGHT, 1);
    replayBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    replayContainer.add(replayBg);
    const replayTxt = this.scene.add.text(0, 0, 'NOUVELLE PARTIE', {
      fontSize: Math.max(10, Math.round(W * 0.011)) + 'px',
      fontFamily: 'monospace',
      color: '#000000',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    replayContainer.add(replayTxt);
    const replayHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    replayContainer.add(replayHit);
    replayHit.on('pointerdown', () => {
      this.currentState = null;
      this.previousState = null;
      this.isPlaying = false;
      this.seatBets = {};
      this.refreshBalance();
      this.clearElements();
      this.renderBetScreen();
    });
    replayHit.on('pointerover', () => replayContainer.setScale(1.05));
    replayHit.on('pointerout', () => replayContainer.setScale(1.0));
    this.elements.push(replayContainer);

    /* RETOUR button */
    const retourContainer = this.scene.add.container(cx + W * 0.1, H * 0.78).setDepth(105);
    const retourBg = this.scene.add.graphics();
    retourBg.fillStyle(0x333333, 1);
    retourBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 6);
    retourContainer.add(retourBg);
    const retourTxt = this.scene.add.text(0, 0, 'RETOUR', {
      fontSize: Math.max(10, Math.round(W * 0.011)) + 'px',
      fontFamily: 'monospace',
      color: '#aaaaaa',
    }).setOrigin(0.5);
    retourContainer.add(retourTxt);
    const retourHit = this.scene.add.zone(0, 0, btnW, btnH).setInteractive({ useHandCursor: true });
    retourContainer.add(retourHit);
    retourHit.on('pointerdown', () => this.onBack());
    retourHit.on('pointerover', () => retourContainer.setAlpha(0.8));
    retourHit.on('pointerout', () => retourContainer.setAlpha(1));
    this.elements.push(retourContainer);
  }

  /* ================================================================== */
  /*  CARD DRAWING                                                       */
  /* ================================================================== */

  private drawCard(
    x: number,
    y: number,
    card: { suit: string; value: string; faceDown?: boolean },
    cardW: number,
    cardH: number,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y).setDepth(100);
    const g = this.scene.add.graphics();
    const W = this.scene.scale.width;
    const cornerRadius = Math.max(4, cardW * 0.1);

    if (card.faceDown) {
      /* Shadow */
      g.fillStyle(0x000000, 0.3);
      g.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 2, cardW, cardH, cornerRadius);

      /* Card back */
      g.fillStyle(CARD_BACK_COLOR, 1);
      g.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);
      g.lineStyle(2, CARD_BACK_PATTERN, 0.8);
      g.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);

      /* Diamond pattern */
      g.lineStyle(1, CARD_BACK_PATTERN, 0.35);
      const step = Math.max(8, cardW * 0.2);
      for (let py = -cardH / 2 + step; py < cardH / 2 - step * 0.5; py += step) {
        for (let px = -cardW / 2 + step; px < cardW / 2 - step * 0.5; px += step) {
          const dSize = step * 0.35;
          g.beginPath();
          g.moveTo(px, py - dSize);
          g.lineTo(px + dSize, py);
          g.lineTo(px, py + dSize);
          g.lineTo(px - dSize, py);
          g.closePath();
          g.strokePath();
        }
      }

      /* Center decorative border */
      g.lineStyle(1, CARD_BACK_PATTERN, 0.5);
      g.strokeRoundedRect(-cardW / 2 + 5, -cardH / 2 + 5, cardW - 10, cardH - 10, Math.max(2, cornerRadius - 2));

      container.add(g);
    } else {
      /* Shadow */
      g.fillStyle(0x000000, 0.3);
      g.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 2, cardW, cardH, cornerRadius);

      /* Card face */
      g.fillStyle(0xffffff, 1);
      g.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);
      g.lineStyle(1, 0xaaaaaa, 1);
      g.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);
      container.add(g);

      const color = suitColor(card.suit);
      const symbol = SUIT_SYMBOLS[card.suit] || '?';
      const valueFontSize = Math.max(9, Math.round(cardW * 0.22));
      const smallSuitSize = Math.max(7, Math.round(cardW * 0.16));
      const centerSuitSize = Math.max(14, Math.round(cardW * 0.4));

      /* Value top-left */
      const valueTxt = this.scene.add.text(-cardW / 2 + cardW * 0.1, -cardH / 2 + cardH * 0.04, card.value, {
        fontSize: valueFontSize + 'px',
        fontFamily: 'monospace',
        color,
        fontStyle: 'bold',
      }).setDepth(101);
      container.add(valueTxt);

      /* Small suit under value */
      const topSuit = this.scene.add.text(-cardW / 2 + cardW * 0.14, -cardH / 2 + cardH * 0.04 + valueFontSize, symbol, {
        fontSize: smallSuitSize + 'px',
        fontFamily: 'serif',
        color,
      }).setDepth(101);
      container.add(topSuit);

      /* Large suit center */
      const suitTxt = this.scene.add.text(0, 2, symbol, {
        fontSize: centerSuitSize + 'px',
        fontFamily: 'serif',
        color,
      }).setOrigin(0.5).setDepth(101);
      container.add(suitTxt);

      /* Value bottom-right (rotated) */
      const bottomVal = this.scene.add.text(cardW / 2 - cardW * 0.1, cardH / 2 - cardH * 0.04, card.value, {
        fontSize: valueFontSize + 'px',
        fontFamily: 'monospace',
        color,
        fontStyle: 'bold',
      }).setOrigin(1, 1).setDepth(101);
      container.add(bottomVal);

      const bottomSuit = this.scene.add.text(cardW / 2 - cardW * 0.14, cardH / 2 - cardH * 0.04 - valueFontSize, symbol, {
        fontSize: smallSuitSize + 'px',
        fontFamily: 'serif',
        color,
      }).setOrigin(1, 1).setDepth(101);
      container.add(bottomSuit);
    }

    return container;
  }

  /* ================================================================== */
  /*  ANIMATED GAME RENDERING                                            */
  /* ================================================================== */

  private renderGameAnimated(state: MultiHandBlackjackState, prevState: MultiHandBlackjackState | null): void {
    this.isPlaying = true;
    this.drawTable();
    this.drawTableText();
    this.drawGameInfoBar(state);
    this.drawShoe();
    this.renderBackButton();

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;
    const cardW = Math.min(W * 0.045, 60);
    const cardH = cardW * 1.44;
    const cardSpacing = Math.min(cardW * 1.15, W * 0.055);

    /* ---- Dealer ---- */
    const dealerLabel = this.scene.add.text(cx, H * 0.10, 'CROUPIER', {
      fontSize: Math.max(12, Math.round(W * 0.015)) + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(100);
    this.elements.push(dealerLabel);

    const dealerScoreText = state.dealerScore !== null ? `${state.dealerScore}` : '?';
    this.renderScoreBadge(cx + W * 0.12, H * 0.10, dealerScoreText);

    const dealerCardY = H * 0.19;
    const dealerCardStartX = cx - ((state.dealerCards.length - 1) * cardSpacing) / 2;
    const prevDealerCount = prevState ? prevState.dealerCards.length : 0;

    state.dealerCards.forEach((card, i) => {
      const targetX = dealerCardStartX + i * cardSpacing;
      const container = this.drawCard(targetX, dealerCardY, card, cardW, cardH);
      this.elements.push(container);

      if (i >= prevDealerCount) {
        this.animateCardDeal(container, targetX, dealerCardY, i * 200);
      }

      if (prevState && i < prevDealerCount && prevState.dealerCards[i].faceDown && !card.faceDown) {
        this.animateCardFlip(container, card, cardW, cardH);
      }
    });

    /* ---- Player hands ---- */
    const numHands = state.hands.length;
    const handSpotPositions = this.getHandSpotPositions(state);
    const playerCardY = H * 0.36;

    /* Dim unused seats */
    for (let s = 0; s < SEAT_COUNT; s++) {
      const pos = this.getSeatPosition(s);
      const isUsed = handSpotPositions.some(hp => hp.seatIndex === s);
      if (!isUsed) {
        this.renderDimSeat(pos.x, pos.y);
      }
    }

    state.hands.forEach((hand, hi) => {
      const hp = handSpotPositions[hi];
      if (!hp) return;
      const spotX = hp.x;
      const spotY = hp.y;
      const isActive = state.activeHandIndex === hi && !state.isAllComplete;

      if (isActive) {
        const glowG = this.scene.add.graphics().setDepth(98);
        glowG.lineStyle(3, GOLD_BRIGHT, 0.7);
        glowG.strokeCircle(spotX, spotY, Math.min(W * 0.04, 36) + 8);
        glowG.lineStyle(6, GOLD_BRIGHT, 0.2);
        glowG.strokeCircle(spotX, spotY, Math.min(W * 0.04, 36) + 14);
        this.elements.push(glowG);
      }

      this.renderGameSpot(spotX, spotY, hand, isActive);

      /* Cards with animation */
      const prevHandCards = prevState && prevState.hands[hi] ? prevState.hands[hi].cards.length : 0;
      const cardY = playerCardY + (hand.isSplitHand ? hi * cardH * 0.12 : 0);
      const fanSpacing = cardSpacing * 0.75;
      const handCardStartX = spotX - ((hand.cards.length - 1) * fanSpacing) / 2;

      hand.cards.forEach((card, ci) => {
        const targetX = handCardStartX + ci * fanSpacing;
        const container = this.drawCard(targetX, cardY, card, cardW, cardH);
        this.elements.push(container);

        if (ci >= prevHandCards) {
          this.animateCardDeal(container, targetX, cardY, ci * 200);
        }
      });

      /* Score badge */
      const scoreBadgeX = spotX + ((hand.cards.length - 1) * fanSpacing) / 2 + cardW * 0.6;
      this.renderScoreBadge(scoreBadgeX, cardY, `${hand.score}`);

      /* Bet badge */
      const betBadgeX = spotX - ((hand.cards.length - 1) * fanSpacing) / 2 - cardW * 0.6;
      this.renderBetBadge(betBadgeX, cardY, hand.betAmount, hand.usedDirty);

      /* Hand label */
      if (numHands > 1) {
        const handLabel = this.scene.add.text(spotX, cardY - cardH / 2 - Math.max(10, H * 0.012),
          hand.isSplitHand ? `Split ${hand.splitDepth}` : `Main ${hand.spotIndex + 1}`, {
            fontSize: Math.max(9, Math.round(W * 0.01)) + 'px',
            fontFamily: 'monospace',
            color: isActive ? GOLD_BRIGHT_HEX : '#aaaaaa',
            fontStyle: isActive ? 'bold' : 'normal',
          }).setOrigin(0.5).setDepth(100);
        this.elements.push(handLabel);
      }

      /* Outcome */
      if (hand.isComplete && state.isAllComplete) {
        this.renderHandOutcome(spotX, cardY - cardH / 2 - (numHands > 1 ? Math.max(24, H * 0.03) : Math.max(14, H * 0.016)), hand);
      }
    });

    /* Insurance phase */
    if (state.insuranceOffered && !state.insurancePhaseComplete) {
      this.renderInsuranceOverlay(state);
      return;
    }

    if (state.isAllComplete) {
      this.renderFinalOutcome(state);
    } else {
      this.renderActionButtons(state);

      if (numHands > 1 && state.activeHandIndex >= 0) {
        const indicatorTxt = this.scene.add.text(cx, H * 0.93,
          `Main ${state.activeHandIndex + 1} / ${numHands}`, {
            fontSize: Math.max(11, Math.round(W * 0.013)) + 'px',
            fontFamily: 'monospace',
            color: GOLD_BRIGHT_HEX,
            fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(100);
        this.elements.push(indicatorTxt);
      }
    }
  }

  /* ================================================================== */
  /*  CARD ANIMATIONS                                                    */
  /* ================================================================== */

  private animateCardDeal(
    container: Phaser.GameObjects.Container,
    targetX: number,
    targetY: number,
    delay: number = 0,
  ): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const deckX = W * 0.88;
    const deckY = H * 0.09;
    container.setPosition(deckX, deckY);
    container.setAlpha(0);
    container.setScale(0.5);

    const tw = this.scene.tweens.add({
      targets: container,
      x: targetX,
      y: targetY,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 250,
      delay,
      ease: 'Power2',
    });
    this.tweens.push(tw);
  }

  private animateCardFlip(
    container: Phaser.GameObjects.Container,
    card: { suit: string; value: string },
    cardW: number,
    cardH: number,
    onComplete?: () => void,
  ): void {
    const cornerRadius = Math.max(4, cardW * 0.1);

    const tw = this.scene.tweens.add({
      targets: container,
      scaleX: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        container.removeAll(true);

        const g = this.scene.add.graphics();
        g.fillStyle(0x000000, 0.3);
        g.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 2, cardW, cardH, cornerRadius);
        g.fillStyle(0xffffff, 1);
        g.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);
        g.lineStyle(1, 0xaaaaaa, 1);
        g.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerRadius);
        container.add(g);

        const color = suitColor(card.suit);
        const symbol = SUIT_SYMBOLS[card.suit] || '?';
        const valueFontSize = Math.max(9, Math.round(cardW * 0.22));
        const smallSuitSize = Math.max(7, Math.round(cardW * 0.16));
        const centerSuitSize = Math.max(14, Math.round(cardW * 0.4));

        const valueTxt = this.scene.add.text(-cardW / 2 + cardW * 0.1, -cardH / 2 + cardH * 0.04, card.value, {
          fontSize: valueFontSize + 'px',
          fontFamily: 'monospace',
          color,
          fontStyle: 'bold',
        }).setDepth(101);
        container.add(valueTxt);

        const topSuit = this.scene.add.text(-cardW / 2 + cardW * 0.14, -cardH / 2 + cardH * 0.04 + valueFontSize, symbol, {
          fontSize: smallSuitSize + 'px',
          fontFamily: 'serif',
          color,
        }).setDepth(101);
        container.add(topSuit);

        const suitTxt = this.scene.add.text(0, 2, symbol, {
          fontSize: centerSuitSize + 'px',
          fontFamily: 'serif',
          color,
        }).setOrigin(0.5).setDepth(101);
        container.add(suitTxt);

        const bottomVal = this.scene.add.text(cardW / 2 - cardW * 0.1, cardH / 2 - cardH * 0.04, card.value, {
          fontSize: valueFontSize + 'px',
          fontFamily: 'monospace',
          color,
          fontStyle: 'bold',
        }).setOrigin(1, 1).setDepth(101);
        container.add(bottomVal);

        const bottomSuit = this.scene.add.text(cardW / 2 - cardW * 0.14, cardH / 2 - cardH * 0.04 - valueFontSize, symbol, {
          fontSize: smallSuitSize + 'px',
          fontFamily: 'serif',
          color,
        }).setOrigin(1, 1).setDepth(101);
        container.add(bottomSuit);

        const tw2 = this.scene.tweens.add({
          targets: container,
          scaleX: 1,
          duration: 150,
          ease: 'Power2',
          onComplete: () => onComplete?.(),
        });
        this.tweens.push(tw2);
      },
    });
    this.tweens.push(tw);
  }

  /* ================================================================== */
  /*  SERVER COMMUNICATION                                               */
  /* ================================================================== */

  private sendAction(action: string, extra?: Record<string, unknown>): void {
    console.log(`[BLACKJACK] sendAction: action=${action}`, extra || '');
    const payload: Record<string, unknown> = { buildingId: this.building.id, action };
    if (extra) Object.assign(payload, extra);
    socketManager.sendAction('CASINO_PLAY_BLACKJACK', payload);
    socketManager.onceActionResult((result: any) => {
      if (result.success && result.data) {
        this.previousState = this.currentState;
        const data = result.data as Record<string, any>;
        this.currentState = (data.details || data) as MultiHandBlackjackState;

        /* Update balances */
        if (data.newClean !== undefined) {
          this.balanceClean = data.newClean;
          try { (this.scene.scene.get('GameScene') as any).moneyClean = data.newClean; } catch { /* ok */ }
        }
        if (data.newDirty !== undefined) {
          this.balanceDirty = data.newDirty;
          try { (this.scene.scene.get('GameScene') as any).moneyDirty = data.newDirty; } catch { /* ok */ }
        }

        console.log(`[BLACKJACK] Result received: hands=${this.currentState.hands.length}, activeHandIndex=${this.currentState.activeHandIndex}, dealerCards=[${this.currentState.dealerCards.map(c => c.faceDown ? '??' : c.value + c.suit).join(',')}], dealerScore=${this.currentState.dealerScore}, isAllComplete=${this.currentState.isAllComplete}, insuranceOffered=${this.currentState.insuranceOffered}, insurancePhaseComplete=${this.currentState.insurancePhaseComplete}, totalPayout=${this.currentState.totalPayout ?? 0}`);
        this.currentState.hands.forEach((h, i) => {
          console.log(`[BLACKJACK]   hand[${i}]: cards=[${h.cards.map(c => c.value + c.suit).join(',')}], score=${h.score}, spotIndex=${h.spotIndex}, isSplit=${h.isSplitHand}, outcome=${h.outcome || 'in-progress'}, payout=${h.payout ?? 0}, canHit=${h.canHit}, canSplit=${h.canSplit}, canSurrender=${h.canSurrender}`);
        });

        this.clearElements();
        this.renderGameAnimated(this.currentState, this.previousState);
      } else if (result.error) {
        this.showError(result.error);
      }
    });
  }

  private showError(message: string): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const errTxt = this.scene.add.text(W / 2, H * 0.65, message, {
      fontSize: Math.max(12, Math.round(W * 0.013)) + 'px',
      fontFamily: 'monospace',
      color: '#ff4444',
      backgroundColor: '#220000',
      padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(200);
    this.elements.push(errTxt);
    const timer = this.scene.time.delayedCall(2500, () => {
      errTxt.destroy();
    });
    this.timers.push(timer as any);
  }

  /* ================================================================== */
  /*  HELPERS                                                            */
  /* ================================================================== */

  private getTotalBet(): number {
    let total = 0;
    for (const key of Object.keys(this.seatBets)) {
      total += this.seatBets[Number(key)].amount;
    }
    return total;
  }

  private getActiveSeatCount(): number {
    return Object.keys(this.seatBets).filter(k => this.seatBets[Number(k)].amount > 0).length;
  }

  private buildHandsFromBets(): { betAmount: number; useDirty: boolean }[] {
    const hands: { betAmount: number; useDirty: boolean }[] = [];
    /* Order by seat index (1, 2, 3) */
    const sortedKeys = Object.keys(this.seatBets).map(Number).sort((a, b) => a - b);
    for (const seatIdx of sortedKeys) {
      const bet = this.seatBets[seatIdx];
      if (bet && bet.amount > 0) {
        hands.push({ betAmount: bet.amount, useDirty: bet.useDirty });
      }
    }
    return hands;
  }

  private getNearestChipColor(amount: number): number {
    /* Find the highest chip denomination that divides into the amount,
       or the closest one for display purposes */
    for (let i = CHIP_DENOMINATIONS.length - 1; i >= 0; i--) {
      if (amount >= CHIP_DENOMINATIONS[i]) return CHIP_DENOMINATIONS[i];
    }
    return CHIP_DENOMINATIONS[0];
  }

  private refreshBalance(): void {
    try {
      const gameScene = this.scene.scene.get('GameScene') as any;
      this.balanceDirty = gameScene.moneyDirty ?? 0;
      this.balanceClean = gameScene.moneyClean ?? 0;
    } catch { /* ok */ }
  }

  /* ================================================================== */
  /*  CLEANUP                                                            */
  /* ================================================================== */

  private clearElements(): void {
    this.tweens.forEach(t => { try { t.stop(); } catch { /* ok */ } });
    this.tweens = [];
    this.timers.forEach(t => { try { t.destroy(); } catch { /* ok */ } });
    this.timers = [];
    this.chipSelectors.forEach(c => { try { c.destroy(); } catch { /* ok */ } });
    this.chipSelectors = [];
    this.elements.forEach(e => { try { e.destroy(); } catch { /* ok */ } });
    this.elements = [];
  }
}
